import { useState } from 'react';
import { X, AlertTriangle, Trash2 } from 'lucide-react';
import { permanentDelete } from '../api/recycleBin';

const PermanentDeleteModal = ({ item, isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');

  const handleDelete = async () => {
    if (!reason.trim()) {
      setError('Please provide a reason for permanent deletion');
      return;
    }

    if (confirmText !== 'PERMANENTLY DELETE') {
      setError('Please type PERMANENTLY DELETE to confirm');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await permanentDelete(item.id, reason);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to permanently delete item');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <AlertTriangle className="h-6 w-6 text-red-600 mr-2" />
              Permanent Delete
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Warning */}
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertTriangle className="h-6 w-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-red-900 mb-1">Warning: This action cannot be undone!</h4>
                  <p className="text-sm text-red-800">
                    This item will be permanently deleted from the database. This action is irreversible
                    and cannot be recovered.
                  </p>
                </div>
              </div>
            </div>

            {/* Item Details */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">Item to be Permanently Deleted</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">Type:</span>
                  <span className="ml-2 font-medium text-gray-900 capitalize">
                    {item?.entity_type?.replace(/_/g, ' ')}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Entity ID:</span>
                  <span className="ml-2 font-medium text-gray-900">{item?.entity_id}</span>
                </div>
                <div>
                  <span className="text-gray-500">Deleted:</span>
                  <span className="ml-2 font-medium text-gray-900">{formatDate(item?.deleted_at)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Deleted By:</span>
                  <span className="ml-2 font-medium text-gray-900">{item?.deleted_by_name || 'Unknown'}</span>
                </div>
                {item?.deletion_reason && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Original Deletion Reason:</span>
                    <span className="ml-2 text-gray-900">{item.deletion_reason}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Data Preview */}
            {item?.snapshot && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Data Preview</h4>
                <div className="bg-gray-50 rounded p-3 max-h-40 overflow-y-auto">
                  <pre className="text-xs text-gray-700">
                    {JSON.stringify(item.snapshot, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* Reason Input */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Reason for Permanent Deletion <span className="text-red-500">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500"
                placeholder="Enter a detailed reason for permanent deletion..."
              />
              <p className="mt-1 text-xs text-gray-500">
                This reason will be logged for audit purposes
              </p>
            </div>

            {/* Confirmation Input */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Type <span className="font-bold text-red-600">PERMANENTLY DELETE</span> to confirm <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500"
                placeholder="PERMANENTLY DELETE"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Impact Warning */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-medium text-yellow-900 mb-2">Impact of Deletion</h4>
              <ul className="text-sm text-yellow-800 space-y-1 ml-4 list-disc">
                <li>This item will be immediately removed from the database</li>
                <li>All associated data will be permanently lost</li>
                <li>This action will be logged in the audit trail</li>
                <li>Referenced items may be affected if foreign keys exist</li>
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={loading || !reason.trim() || confirmText !== 'PERMANENTLY DELETE'}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-5 w-5 mr-2" />
                  Permanently Delete
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermanentDeleteModal;
